import React, { useMemo } from 'react'
import styled from 'styled-components'
import { zip } from 'lodash'
// @ts-ignore
import { Table as NbUiTable } from '@netbasequid/core'
// @ts-ignore
import { ref } from '@netbasequid/theme'
import { ColDefs } from 'components/visualization/Table/types/groupTable'
import { getCellIndexOffsetByColDefs } from 'components/visualization/Table'
import { useDisplayTheme } from 'hooks/useUiState'
import { Theme } from 'features/ui/types'
import { AutomationId } from 'utils/automationUtil'
import HeaderRenderer from './HeaderRenderer'
import CellRenderer from './CellRenderer'
import FooterRenderer from './FooterRenderer'
import SectionDivider from './SectionDivider'
import useSort from './sort'
import { TableConfigs } from './types'

const StyledTableContainer = styled.div<{ $mode: Theme }>`
  width: 100%;
  height: 100%;
  tr[data-testid^='group-table-divider'] {
    background-color: ${({ $mode }) =>
      $mode === 'light' ? ref.color.gray[200] : ref.color.gray[1300]};
    td {
      background-color: ${({ $mode }) =>
        $mode === 'light' ? ref.color.gray[200] : ref.color.gray[1300]};
    }
  }
  tr[data-testid^='group-table-footer-row'] {
    td {
      background-color: ${({ $mode }) =>
        $mode === 'light' ? ref.color.gray[100] : ref.color.gray[1380]};
    }
  }
`

type TableProps<TRow, TContext, TField extends keyof TRow> = {
  groupedRowData: TRow[][]
  groupedContext: TContext[]
  colDefs: ColDefs<TRow, TContext, TField>
  footerContext: TContext
  isDisplayFoot: boolean
  groupTitles?: string[]
  configs?: TableConfigs<TRow>
}

const GroupTable = <TRow, TContext, TField extends keyof TRow>({
  groupedRowData,
  groupedContext,
  colDefs,
  footerContext,
  isDisplayFoot,
  groupTitles,
  configs
}: TableProps<TRow, TContext, TField>) => {
  const theme = useDisplayTheme() as Theme
  const cellIndexOffset = getCellIndexOffsetByColDefs<TRow, TContext, TField>(colDefs)

  const [sort, order, orderBy, handleRequestSort] = useSort<TRow>(configs?.colSortingConfigs)
  const sortedGroupedRowData = groupedRowData.map((rowData) => sort(rowData, colDefs))

  const groupFooterRendersByRow = useMemo(() => {
    const groupFooterRendersByCol = colDefs.map((colDef) => [
      ...(colDef.customGroupFooterRenders || [])
    ])
    return zip(...groupFooterRendersByCol)
  }, [colDefs])

  const footerRendersByRow = useMemo(() => {
    const footerRendersByCol = colDefs.map((colDef) => [...(colDef.customFooterRenders || [])])
    return zip(...footerRendersByCol)
  }, [colDefs])

  return (
    <StyledTableContainer $mode={theme}>
      <NbUiTable mode={theme} order={order} orderBy={orderBy} onRequestSort={handleRequestSort}>
        <NbUiTable.TableHead>
          <NbUiTable.TableRow>
            {colDefs.map((colDef, colIndex) => (
              <HeaderRenderer
                testId={`${AutomationId.table.header}-${colIndex}`}
                key={colDef.field as React.Key}
                colDef={colDef}
                context={{ theme }}
              />
            ))}
          </NbUiTable.TableRow>
        </NbUiTable.TableHead>

        {sortedGroupedRowData.map((rowData, rowDataIndex) => (
          <NbUiTable.TableBody
            // eslint-disable-next-line react/no-array-index-key
            key={rowDataIndex}
            testId={`${AutomationId.groupTable.body}-${rowDataIndex}`}>
            {groupTitles?.[rowDataIndex] && (
              <SectionDivider
                columnCount={colDefs.length}
                testId={`${AutomationId.groupTable.divider}-${rowDataIndex}`}>
                {groupTitles?.[rowDataIndex]}
              </SectionDivider>
            )}
            {rowData.map((row, rowIndex) => (
              // eslint-disable-next-line react/no-array-index-key
              <NbUiTable.TableRow
                testId={`${AutomationId.table.row}-${rowIndex}`}
                key={configs?.keyField ? row[configs?.keyField] : rowIndex}>
                {colDefs.map((colDef, colIndex) => (
                  <CellRenderer
                    key={colDef.field as React.Key}
                    colDef={colDef}
                    context={{ row, ...groupedContext[rowDataIndex], theme }}
                    indexOffset={cellIndexOffset[colIndex]}
                  />
                ))}
              </NbUiTable.TableRow>
            ))}
            {isDisplayFoot &&
              groupFooterRendersByRow.map((footerRenders, rowIndex) => (
                <NbUiTable.TableRow
                  testId={`${AutomationId.groupTable.footerRow}-${rowIndex}`}
                  // eslint-disable-next-line react/no-array-index-key
                  key={rowIndex}>
                  {footerRenders.map((footerRender, colIndex) => (
                    <FooterRenderer
                      testId={`${AutomationId.table.footer}-${colIndex}`}
                      // eslint-disable-next-line react/no-array-index-key
                      key={colDefs[colIndex].field as React.Key}
                      colDef={colDefs[colIndex]}
                      context={{ ...groupedContext[rowDataIndex], theme }}
                      customRender={footerRender}
                    />
                  ))}
                </NbUiTable.TableRow>
              ))}
          </NbUiTable.TableBody>
        ))}

        {isDisplayFoot && groupedRowData.length > 1 && (
          <NbUiTable.TableFoot>
            {footerRendersByRow.map((footerRenders, rowIndex) => (
              <NbUiTable.TableRow
                testId={`${AutomationId.table.footerRow}-${rowIndex}`}
                // eslint-disable-next-line react/no-array-index-key
                key={rowIndex}>
                {footerRenders.map((footerRender, colIndex) => (
                  <FooterRenderer
                    testId={`${AutomationId.table.footer}-${colIndex}`}
                    // eslint-disable-next-line react/no-array-index-key
                    key={colDefs[colIndex].field as React.Key}
                    colDef={colDefs[colIndex]}
                    context={{ ...footerContext, theme }}
                    customRender={footerRender}
                  />
                ))}
              </NbUiTable.TableRow>
            ))}
          </NbUiTable.TableFoot>
        )}
      </NbUiTable>
    </StyledTableContainer>
  )
}

export default GroupTable
